<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import TableSelector from './components/TableSelector.vue'
import TableStructure from './components/TableStructure.vue'
import TableData from './components/TableData.vue'
import CreateTableModal from './components/CreateTableModal.vue'
import QueryMaker from './components/QueryMaker.vue'
import TableInfoPanel from './components/TableInfoPanel.vue'
import RecordViewer from './components/RecordViewer.vue'
import { useDatabasePopupStore } from '../../stores/databasePopupStore'

const openTabs = ref([])
const activeTabId = ref('')
const showCreateModal = ref(false)
const refreshKey = ref(0)
const nextTabId = ref(1)
const rightPanelCollapsed = ref(false)
const selectedRecord = ref(null)
const selectedRecordColumns = ref([])
const selectedTableName = ref('')
const showRecordViewer = ref(false)
const gridMode = ref(false)
const gridTabs = ref([]) // Up to 4 tabs for grid view
const gridRecordViewers = ref({}) // Track record viewers for each grid tab
const gridLayout = ref('2x2') // '2x2', '2x1', '1x2'

// Resizable sidebar
const sidebarWidth = ref(320) // Default width in pixels (w-80 = 320px)
const isResizing = ref(false)
const startX = ref(0)
const startWidth = ref(0)

// Use global popup store
const popupStore = useDatabasePopupStore()

const activeTab = computed(() => {
  return openTabs.value.find(tab => tab.id === activeTabId.value)
})

const handleTableSelect = (tableName) => {
  // Open data tab by default when selecting a table
  console.log('handleTableSelect called for:', tableName, 'Grid mode:', gridMode.value, 'Grid tabs count:', gridTabs.value.length)
  openTab(tableName, 'data')
}

const openTab = (tableName, type = 'data') => {
  // Check if this specific tab is already open in regular tabs
  const existingTab = openTabs.value.find(tab => tab.tableName === tableName && tab.type === type)
  
  if (existingTab) {
    // Switch to existing tab
    activeTabId.value = existingTab.id
    return
  }
  
  // Check if this specific tab is already open in grid tabs
  const existingGridTab = gridTabs.value.find(tab => tab.tableName === tableName && tab.type === type)
  
  if (existingGridTab) {
    // Already in grid, no need to add again
    return
  }

  let title = ''
  let icon = ''
  
  if (type === 'query') {
    title = 'SQL Query'
    icon = 'fas fa-code'
  } else if (type === 'structure') {
    title = `${tableName} - Structure`
    icon = 'fas fa-cog'
  } else if (type === 'data') {
    title = `${tableName} - Data`
    icon = 'fas fa-table'
  }

  const newTab = {
    id: `tab-${nextTabId.value++}`,
    tableName: tableName,
    title: title,
    type: type,
    icon: icon
  }
  
  if (gridMode.value && canAddToGrid()) {
    // Add to grid if in grid mode and space available
    console.log('Adding to grid:', tableName, type, 'Current grid count:', gridTabs.value.length)
    gridTabs.value.push(newTab)
  } else {
    // Add to regular tabs
    console.log('Adding to regular tabs:', tableName, type, 'Grid mode:', gridMode.value, 'Can add to grid:', canAddToGrid())
    openTabs.value.push(newTab)
    activeTabId.value = newTab.id
  }
}

const openQueryMaker = () => {
  openTab('', 'query')
}

const closeTab = (tabId, event) => {
  event?.stopPropagation()
  
  const tabIndex = openTabs.value.findIndex(tab => tab.id === tabId)
  if (tabIndex === -1) return
  
  openTabs.value.splice(tabIndex, 1)
  
  // If closed tab was active, switch to another tab
  if (activeTabId.value === tabId) {
    if (openTabs.value.length > 0) {
      // Switch to the tab before the closed one, or the first tab
      const newActiveIndex = Math.max(0, tabIndex - 1)
      activeTabId.value = openTabs.value[newActiveIndex]?.id || ''
    } else {
      activeTabId.value = ''
    }
  }
}

const switchTab = (tabId) => {
  activeTabId.value = tabId
}

const closeAllTabs = () => {
  openTabs.value = []
  activeTabId.value = ''
}

const handleTableCreated = () => {
  showCreateModal.value = false
  refreshKey.value++
}

const handleTableDeleted = (deletedTableName) => {
  // Remove any tabs for the deleted table
  const tabsToRemove = openTabs.value.filter(tab => tab.tableName === deletedTableName)
  tabsToRemove.forEach(tab => closeTab(tab.id))
  
  refreshKey.value++
}

const toggleRightPanel = () => {
  rightPanelCollapsed.value = !rightPanelCollapsed.value
}

const handleRecordSelected = (record, columns, tableName = '') => {
  if (gridMode.value) {
    // In grid mode, handle per-tile record selection
    return // Will be handled by individual grid handlers
  } else {
    // In single mode, use global record viewer
    selectedRecord.value = record
    selectedRecordColumns.value = columns
    selectedTableName.value = tableName
    showRecordViewer.value = true
  }
}

const handleRecordDeselected = () => {
  if (gridMode.value) {
    // In grid mode, handle per-tile record deselection
    return // Will be handled by individual grid handlers
  } else {
    // In single mode, use global record viewer
    selectedRecord.value = null
    selectedRecordColumns.value = []
    selectedTableName.value = ''
    showRecordViewer.value = false
  }
}

// Grid-specific record handlers
const handleGridRecordSelected = (tabId, record, columns, tableName) => {
  gridRecordViewers.value[tabId] = {
    show: true,
    record: record,
    columns: columns,
    tableName: tableName
  }
}

const handleGridRecordDeselected = (tabId) => {
  if (gridRecordViewers.value[tabId]) {
    gridRecordViewers.value[tabId].show = false
  }
}

const closeGridRecordViewer = (tabId) => {
  handleGridRecordDeselected(tabId)
}

const openTablePopup = (tableName, type = 'data') => {
  popupStore.addPopupTable(tableName, type)
}

const closeRecordViewer = () => {
  handleRecordDeselected()
}

const editSelectedRecord = () => {
  // This will be handled by the TableData component
  showRecordViewer.value = false
}

// Resize functionality
const startResize = (event) => {
  isResizing.value = true
  startX.value = event.clientX
  startWidth.value = sidebarWidth.value
  document.body.style.cursor = 'col-resize'
  document.body.style.userSelect = 'none'
  
  document.addEventListener('mousemove', doResize)
  document.addEventListener('mouseup', stopResize)
}

const doResize = (event) => {
  if (!isResizing.value) return
  
  const deltaX = event.clientX - startX.value
  const newWidth = startWidth.value + deltaX
  
  // Set min and max width constraints
  const minWidth = 200
  const maxWidth = Math.min(800, window.innerWidth * 0.5) // Max 50% of screen width or 800px
  
  if (newWidth >= minWidth && newWidth <= maxWidth) {
    sidebarWidth.value = newWidth
  }
}

const stopResize = () => {
  isResizing.value = false
  document.body.style.cursor = ''
  document.body.style.userSelect = ''
  
  document.removeEventListener('mousemove', doResize)
  document.removeEventListener('mouseup', stopResize)
}

const validateSidebarWidth = () => {
  const minWidth = 200
  const maxWidth = 800
  
  if (sidebarWidth.value < minWidth) {
    sidebarWidth.value = minWidth
  } else if (sidebarWidth.value > maxWidth) {
    sidebarWidth.value = maxWidth
  }
}

const toggleGridMode = () => {
  gridMode.value = !gridMode.value
  console.log('Grid mode toggled to:', gridMode.value)
  
  if (gridMode.value) {
    // Move current active tab to grid if available
    if (activeTab.value && gridTabs.value.length < 4) {
      const currentTab = activeTab.value
      const tabIndex = openTabs.value.findIndex(tab => tab.id === currentTab.id)
      if (tabIndex !== -1) {
        openTabs.value.splice(tabIndex, 1)
        gridTabs.value.push(currentTab)
        console.log('Moved active tab to grid:', currentTab.tableName)
      }
    }
  } else {
    // Move all grid tabs back to regular tabs
    openTabs.value.push(...gridTabs.value)
    if (gridTabs.value.length > 0) {
      activeTabId.value = gridTabs.value[0].id
    }
    console.log('Moved all grid tabs back to regular tabs, count:', gridTabs.value.length)
    gridTabs.value = []
  }
}

const removeFromGrid = (tabId) => {
  const tabIndex = gridTabs.value.findIndex(tab => tab.id === tabId)
  if (tabIndex !== -1) {
    gridTabs.value.splice(tabIndex, 1)
  }
}

const cycleGridLayout = () => {
  const layouts = ['2x2', '2x1', '1x2']
  const currentIndex = layouts.indexOf(gridLayout.value)
  const nextIndex = (currentIndex + 1) % layouts.length
  gridLayout.value = layouts[nextIndex]
}

const canAddToGrid = () => {
  const currentCount = gridTabs.value.length
  
  if (gridLayout.value === '2x1' || gridLayout.value === '1x2') {
    return currentCount < 2
  } else {
    // 2x2 layout
    return currentCount < 4
  }
}

const getGridClasses = () => {
  const tabCount = gridTabs.value.length
  
  if (gridLayout.value === '2x1') {
    // Horizontal layout - 2 columns, 1 row (max 2 tables)
    return tabCount === 1 ? 'grid-cols-1' : 'grid-cols-2'
  } else if (gridLayout.value === '1x2') {
    // Vertical layout - 1 column, 2 rows (max 2 tables)
    return tabCount === 1 ? 'grid-cols-1' : 'grid-cols-1 grid-rows-2'
  } else {
    // 2x2 layout (default) - handles 1-4 tables
    if (tabCount === 1) return 'grid-cols-1'
    if (tabCount === 2) return 'grid-cols-2'
    if (tabCount === 3) return 'grid-cols-2 grid-rows-2'
    if (tabCount === 4) return 'grid-cols-2 grid-rows-2'
    return 'grid-cols-2 grid-rows-2'
  }
}

// Keyboard shortcuts
const handleKeyDown = (event) => {
  // Ctrl/Cmd + W to close current tab
  if ((event.ctrlKey || event.metaKey) && event.key === 'w') {
    event.preventDefault()
    if (activeTabId.value) {
      closeTab(activeTabId.value)
    }
  }
  
  // Ctrl/Cmd + Shift + W to close all tabs
  if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'W') {
    event.preventDefault()
    closeAllTabs()
  }
  
  // Ctrl/Cmd + Tab to switch to next tab
  if ((event.ctrlKey || event.metaKey) && event.key === 'Tab') {
    event.preventDefault()
    switchToNextTab()
  }
}

const switchToNextTab = () => {
  if (openTabs.value.length <= 1) return
  
  const currentIndex = openTabs.value.findIndex(tab => tab.id === activeTabId.value)
  const nextIndex = (currentIndex + 1) % openTabs.value.length
  activeTabId.value = openTabs.value[nextIndex].id
}

const handleTableChanged = (tableName) => {
  // Switch to the selected table by opening it in a new tab or switching to existing tab
  const existingTab = openTabs.value.find(tab => tab.tableName === tableName && tab.type === 'data')
  
  if (existingTab) {
    // Switch to existing tab
    switchTab(existingTab.id)
  } else {
    // Open new tab for the table
    openTab(tableName, 'data')
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleKeyDown)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeyDown)
  // Clean up resize event listeners
  document.removeEventListener('mousemove', doResize)
  document.removeEventListener('mouseup', stopResize)
})
</script>

<template>
  <div class="h-full flex flex-col">
    <!-- Header -->
    <div class="bg-white border-b border-gray-200 px-4 py-3">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-lg font-semibold text-gray-900">Database Table Manager</h1>
          <p class="text-gray-600 text-sm">Manage database tables and their data</p>
        </div>
        <div class="flex items-center space-x-2">
          <!-- Sidebar Width Control -->
          <div class="flex items-center text-sm text-gray-600">
            <label class="mr-2">Panel Width:</label>
            <input
              v-model.number="sidebarWidth"
              type="number"
              min="200"
              max="800"
              class="w-16 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              @input="validateSidebarWidth"
            />
            <span class="ml-1 text-xs text-gray-500">px</span>
          </div>
          
          <!-- Toggle All Popups -->
          <button
            v-if="popupStore.popupTables.length > 0"
            @click="popupStore.toggleAllPopupsVisibility"
            class="px-3 py-1.5 rounded-md flex items-center text-sm transition-colors"
            :class="popupStore.allPopupsHidden ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'"
            :title="popupStore.allPopupsHidden ? `Show All Popups (${popupStore.popupTables.length})` : `Hide All Popups (${popupStore.getVisiblePopupsCount()})`"
          >
            <i :class="popupStore.allPopupsHidden ? 'fas fa-eye' : 'fas fa-eye-slash'" class="mr-1.5"></i>
            {{ popupStore.allPopupsHidden ? 'Show' : 'Hide' }} Popups
            <span class="ml-1 bg-black bg-opacity-20 px-1.5 py-0.5 rounded text-xs">
              {{ popupStore.allPopupsHidden ? popupStore.popupTables.length : popupStore.getVisiblePopupsCount() }}
            </span>
          </button>
          
          <button 
            @click="toggleGridMode"
            class="px-3 py-1.5 rounded-md flex items-center text-sm transition-colors"
            :class="gridMode ? 'bg-orange-600 hover:bg-orange-700 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'"
            :title="gridMode ? 'Switch to Single View' : 'Switch to Grid View'"
          >
            <i :class="gridMode ? 'fas fa-th-large' : 'fas fa-square'" class="mr-1.5"></i>
            {{ gridMode ? 'Grid' : 'Single' }}
          </button>
          <button 
            v-if="gridMode"
            @click="cycleGridLayout"
            class="px-3 py-1.5 rounded-md flex items-center text-sm bg-orange-500 hover:bg-orange-600 text-white transition-colors"
            :title="`Current: ${gridLayout} - Click to cycle layout`"
          >
            <i class="fas fa-expand-arrows-alt mr-1.5"></i>
            {{ gridLayout }}
          </button>
          <button 
            @click="openQueryMaker"
            class="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-md flex items-center text-sm"
          >
            <i class="fas fa-code mr-1.5"></i>
            SQL Query
          </button>
          <button 
            @click="showCreateModal = true"
            class="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-md flex items-center text-sm"
          >
            <i class="fas fa-plus mr-1.5"></i>
            Create Table
          </button>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex-1 flex overflow-hidden">
      <!-- Left Sidebar - Table List -->
      <div 
        :style="{ width: sidebarWidth + 'px' }" 
        class="bg-gray-50 border-r border-gray-200 overflow-y-auto flex-shrink-0"
      >
        <TableSelector 
          :key="refreshKey"
          :selected-table="activeTab?.tableName || ''"
          :grid-mode="gridMode"
          :grid-tabs="gridTabs"
          :grid-layout="gridLayout"
          @table-select="handleTableSelect"
          @table-deleted="handleTableDeleted"
          @open-structure="(tableName) => openTab(tableName, 'structure')"
          @open-data="(tableName) => openTab(tableName, 'data')"
          @open-popup="(tableName, type) => openTablePopup(tableName, type)"
        />
      </div>
      
      <!-- Resize Handle -->
      <div 
        class="w-1 bg-transparent hover:bg-gray-300 cursor-col-resize flex-shrink-0 transition-colors duration-150 group"
        :class="{ 'bg-gray-400': isResizing }"
        @mousedown="startResize"
        title="Drag to resize sidebar"
      >
        <!-- Visible indicator only on hover -->
        <div class="h-full w-full bg-gray-200 opacity-0 group-hover:opacity-100 transition-opacity duration-150" :class="{ 'opacity-100': isResizing }"></div>
      </div>

      <!-- Main Content -->
      <div class="flex-1 flex flex-col overflow-hidden">
        <!-- Grid Mode -->
        <div v-if="gridMode" class="flex-1 p-2 overflow-hidden">
          <div v-if="gridTabs.length === 0" class="h-full flex items-center justify-center text-gray-500">
            <div class="text-center">
              <i class="fas fa-th-large text-6xl mb-4"></i>
              <p class="text-xl">Grid View Mode</p>
              <p class="text-sm text-gray-400 mt-2">Select up to 4 tables to view in a 2x2 grid</p>
            </div>
          </div>
          
          <div v-else class="h-full grid gap-2 overflow-hidden" :class="getGridClasses()">
            <div 
              v-for="tab in gridTabs" 
              :key="tab.id"
              class="bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col min-h-0 h-full"
            >
              <!-- Grid Tab Header -->
              <div class="bg-gray-50 border-b border-gray-200 px-3 py-2 flex items-center justify-between flex-shrink-0">
                <div class="flex items-center">
                  <i :class="tab.icon || 'fas fa-table'" class="mr-2 text-xs text-gray-600"></i>
                  <span class="text-xs font-medium text-gray-900 truncate">{{ tab.title }}</span>
                </div>
                <button
                  @click="removeFromGrid(tab.id)"
                  class="text-gray-400 hover:text-gray-600 text-xs"
                  title="Remove from grid"
                >
                  <i class="fas fa-times"></i>
                </button>
              </div>
              
              <!-- Grid Tab Content -->
              <div class="flex-1 overflow-hidden flex min-h-0">
                <!-- Main Content Area -->
                <div class="flex-1 overflow-hidden min-h-0" :class="gridRecordViewers[tab.id]?.show ? 'w-2/3' : 'w-full'">
                  <!-- Query Maker -->
                  <div v-if="tab.type === 'query'" class="h-full">
                    <QueryMaker />
                  </div>

                  <!-- Table Structure -->
                  <div v-else-if="tab.type === 'structure'" class="h-full">
                    <TableStructure :table-name="tab.tableName" />
                  </div>

                  <!-- Table Data -->
                  <div v-else-if="tab.type === 'data'" class="h-full">
                    <TableData 
                      :table-name="tab.tableName" 
                      @record-selected="(record, columns) => handleGridRecordSelected(tab.id, record, columns, tab.tableName)"
                      @record-deselected="() => handleGridRecordDeselected(tab.id)"
                      @table-changed="(tableName) => handleTableChanged(tableName)"
                    />
                  </div>
                </div>

                <!-- Inline Record Viewer for this tile -->
                <div 
                  v-if="gridRecordViewers[tab.id]?.show" 
                  class="w-1/3 border-l border-gray-200 bg-gray-50 overflow-hidden min-h-0 flex flex-col"
                >
                  <RecordViewer
                    :show="true"
                    :record="gridRecordViewers[tab.id].record"
                    :columns="gridRecordViewers[tab.id].columns"
                    :table-name="gridRecordViewers[tab.id].tableName"
                    :is-inline="true"
                    @close="() => closeGridRecordViewer(tab.id)"
                    @edit="() => {}"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Single Tab Mode -->
        <div v-else class="flex-1 flex flex-col overflow-hidden">
          <!-- Tabs Header -->
          <div v-if="openTabs.length > 0" class="bg-white border-b border-gray-200">
            <div class="flex items-center px-4 pt-3">
              <div class="flex items-center space-x-1 flex-1 overflow-x-auto">
                <div
                  v-for="tab in openTabs"
                  :key="tab.id"
                  @click="switchTab(tab.id)"
                  class="group flex items-center px-3 py-1.5 rounded-t-md cursor-pointer transition-colors min-w-0 flex-shrink-0 text-sm"
                  :class="{
                    'bg-blue-50 text-blue-600 border-b-2 border-blue-500': activeTabId === tab.id,
                    'text-gray-600 hover:bg-gray-50': activeTabId !== tab.id
                  }"
                >
                   <i :class="tab.icon || 'fas fa-table'" class="mr-1.5 flex-shrink-0 text-xs"></i>
                   <span class="truncate max-w-[100px]" :title="tab.title">{{ tab.title }}</span>
                  <button
                    @click="closeTab(tab.id, $event)"
                    class="ml-1.5 text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                  >
                    <i class="fas fa-times text-xs"></i>
                  </button>
                </div>
              </div>
              
              <!-- Tab Actions -->
              <div class="flex items-center space-x-2 ml-4">
                <button
                  v-if="openTabs.length > 1"
                  @click="closeAllTabs"
                  class="text-gray-500 hover:text-gray-700 px-2 py-1 text-xs"
                  title="Close All Tabs"
                >
                  <i class="fas fa-times-circle mr-1"></i>
                  Close All
                </button>
                <span class="text-gray-400 text-xs">{{ openTabs.length }} open</span>
              </div>
            </div>
          </div>

          <!-- Tab Content -->
          <div v-if="!activeTab && !gridMode" class="flex-1 flex items-center justify-center text-gray-500">
            <div class="text-center">
              <i class="fas fa-table text-6xl mb-4"></i>
              <p class="text-xl">Select a table to manage</p>
              <p class="text-sm text-gray-400 mt-2">Tables will open in tabs for easy switching</p>
            </div>
          </div>

          <div v-else-if="activeTab" class="flex-1 flex flex-col min-h-0">
            <!-- Query Maker -->
            <div v-if="activeTab.type === 'query'" class="flex-1 overflow-hidden">
              <QueryMaker />
            </div>

            <!-- Table Structure -->
            <div v-else-if="activeTab.type === 'structure'" class="flex-1 overflow-hidden">
              <div class="h-full flex flex-col">
                <div class="flex-1 overflow-auto">
                  <TableStructure :table-name="activeTab.tableName" />
                </div>
                
                <!-- Quick Actions -->
                <div class="bg-gray-50 border-t border-gray-200 p-2">
                  <div class="flex items-center justify-between">
                    <span class="text-xs text-gray-600">Table: {{ activeTab.tableName }}</span>
                    <button
                      @click="openTab(activeTab.tableName, 'data')"
                      class="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded text-xs flex items-center"
                    >
                      <i class="fas fa-table mr-1"></i>
                      View Data
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Table Data -->
            <div v-else-if="activeTab.type === 'data'" class="flex-1 flex flex-col min-h-0">
              <div class="flex-1 min-h-0">
                <TableData 
                  :table-name="activeTab.tableName" 
                  @record-selected="(record, columns) => handleRecordSelected(record, columns, activeTab.tableName)"
                  @record-deselected="handleRecordDeselected"
                  @table-changed="(tableName) => handleTableChanged(tableName)"
                />
              </div>
              
              <!-- Quick Actions -->
              <div class="bg-gray-50 border-t border-gray-200 p-2 flex-shrink-0">
                <div class="flex items-center justify-between">
                  <span class="text-xs text-gray-600">Table: {{ activeTab.tableName }}</span>
                  <button
                    @click="openTab(activeTab.tableName, 'structure')"
                    class="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-xs flex items-center"
                  >
                    <i class="fas fa-cog mr-1"></i>
                    View Structure
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Info Panel -->
      <RecordViewer
        v-if="showRecordViewer"
        :show="showRecordViewer"
        :record="selectedRecord"
        :columns="selectedRecordColumns"
        :table-name="selectedTableName || activeTab?.tableName || ''"
        @close="closeRecordViewer"
        @edit="editSelectedRecord"
      />
      <TableInfoPanel 
        v-else-if="!gridMode"
        :table-name="activeTab?.tableName || ''"
        :collapsed="rightPanelCollapsed"
        @toggle-collapse="toggleRightPanel"
      />
    </div>

        <!-- Create Table Modal -->
    <CreateTableModal 
      v-if="showCreateModal"
      @close="showCreateModal = false"
      @table-created="handleTableCreated"
    />


   </div>
 </template> 